/**
 * @fileoverview RTK Query bindings for the self-serve advertiser portal.
 *
 * One typed client for the whole M3 wizard spine, so the portal surfaces
 * (WEB-586 checkout success/cancel, WEB-587 wizard steps, WEB-588 status inbox)
 * never hand-roll a `fetch` against these paths:
 *
 * | Hook                                        | Method + path                                                       | Issue    |
 * | ------------------------------------------- | ------------------------------------------------------------------- | -------- |
 * | `useCreateAdvertiserMutation`                | `POST   /v1/advertisers`                                            | WEB-40   |
 * | `useCreateVenueClaimMutation`                | `POST   /v1/advertisers/me/venue-claims`                            | API-1626 |
 * | `useListVenueClaimsQuery`                    | `GET    /v1/advertisers/me/venue-claims`                            | API-1626 |
 * | `useListClaimableVenuesQuery`                | `GET    /v1/advertisers/me/claimable-venues`                        | WEB-587  |
 * | `useListSponsorableEventsQuery`              | `GET    /v1/advertisers/me/sponsorable-events`                      | WEB-587  |
 * | `useCreateSponsoredListingMutation`          | `POST   /v1/advertisers/me/sponsored-listings`                      | API-1627 |
 * | `useListMySponsoredListingsQuery`            | `GET    /v1/advertisers/me/sponsored-listings`                      | API-1630 |
 * | `useCreateSponsoredCheckoutSessionMutation`  | `POST   /v1/advertisers/me/sponsored-listings/:id/checkout-session` | API-1628 |
 * | `useGetSponsoredCheckoutStatusQuery`         | `GET    /v1/advertisers/checkout-status` (public)                   | WEB-1263 |
 *
 * **No advertiser id is ever sent.** Every `/me/*` route resolves the advertiser
 * from the session (`req.user.id → User.advertiserId`), so these hooks take no
 * advertiser argument — there is nothing for a caller to get wrong, and nothing
 * a tampered client could point at someone else's data.
 *
 * Response types come from the generated OpenAPI components (`@loci/types`)
 * rather than being hand-authored here, so a spec change surfaces as a type
 * error instead of silent client/server drift.
 *
 * Mutations that take a body use `queryFn` + `safeParse` (the `savedSearches.ts`
 * pattern) so a malformed payload short-circuits before the network call and
 * reuses the same `@loci/validation` schema the route enforces.
 */
import type { UnknownAction } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import type { BackendApiComponents as components } from '@loci/types';
import {
  createAdvertiserSchema,
  createSelfServeSponsoredListingSchema,
  createVenueClaimSchema,
  type CreateAdvertiserBody,
  type CreateSelfServeSponsoredListingBody,
  type CreateVenueClaimBody,
} from '@loci/validation';

import { lociApi } from '../lociApi.js';

/** Wire payload for a created/linked advertiser (mirrors `toAdvertiserPayload`). */
export type AdvertiserPayload = components['schemas']['AdvertiserPayload'];

/** A venue claim row (API-1626). */
export type VenueClaimPayload = components['schemas']['VenueClaimPayload'];

/**
 * A sponsored listing row. The same shape is returned by self-serve create and
 * by list-mine, so the wizard's confirmation step and the status inbox render
 * off one type — including `status`, `tier`, `rejectedReason`, `refundStatus`,
 * and `refundedAt`.
 */
export type SponsoredListingPayload = components['schemas']['SponsoredListingPayload'];

export type CreateAdvertiserRequest = CreateAdvertiserBody;
export type CreateVenueClaimRequest = CreateVenueClaimBody;
export type CreateSponsoredListingRequest = CreateSelfServeSponsoredListingBody;

export type CreateAdvertiserResponse = components['schemas']['AdvertiserResponse'];
export type VenueClaimResponse = components['schemas']['VenueClaimResponse'];
export type ListVenueClaimsResponse = components['schemas']['VenueClaimListResponse'];

/** A venue the advertiser can claim, carrying the `locationId` the claim needs (WEB-587). */
export type ClaimableVenuePayload = components['schemas']['ClaimableVenuePayload'];
export type ListClaimableVenuesResponse = components['schemas']['ClaimableVenueListResponse'];

/** An event at an approved venue, carrying its PREMIUM eligibility verdict (WEB-587). */
export type SponsorableEventPayload = components['schemas']['SponsorableEventPayload'];
export type ListSponsorableEventsResponse = components['schemas']['SponsorableEventListResponse'];

/** All args optional — the route supplies `cityCode: 'CLT'` and `limit: 25`. */
export interface ListClaimableVenuesRequest {
  q?: string;
  cityCode?: string;
  limit?: number;
}

/** `locationId` narrows to one approved venue; omitted spans every approved claim. */
export interface ListSponsorableEventsRequest {
  locationId?: string;
  limit?: number;
}
export type SponsoredListingResponse = components['schemas']['SponsoredListingResponse'];
export type ListSponsoredListingsResponse = components['schemas']['SponsoredListingListResponse'];

/**
 * Checkout-session create returns a bare `{ url }` — deliberately NOT the
 * `{ data }` envelope its siblings use. Callers redirect the browser to `url`.
 */
export type SponsoredCheckoutSessionResponse =
  components['schemas']['SponsoredCheckoutSessionResponse'];

/** Body of the public `GET /v1/advertisers/checkout-status` (API-1847). */
export type SponsoredCheckoutStatusResponse =
  components['schemas']['SponsoredCheckoutStatusResponse'];

/**
 * Upper bound on the checkout-status read (WEB-1263). The web treats a failed
 * read as "closed", and a read that never answers has to count as failed too,
 * or the advertiser sits on a page that can neither sell nor say it can't.
 */
export const SPONSORED_CHECKOUT_STATUS_TIMEOUT_MS = 5_000;

/** Args for `createSponsoredCheckoutSession` — the listing must be caller-owned. */
export interface CreateSponsoredCheckoutSessionRequest {
  listingId: string;
}

const advertiserEndpoints = lociApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Whether sponsored checkout is open (API-1847). Public and unauthenticated,
     * backed by the same gate the checkout-session routes enforce. The server
     * caches it for 60 s, so the client keeps it for the same window instead of
     * re-asking on every mount.
     */
    getSponsoredCheckoutStatus: builder.query<SponsoredCheckoutStatusResponse, void>({
      query: () => ({
        url: 'v1/advertisers/checkout-status',
        timeout: SPONSORED_CHECKOUT_STATUS_TIMEOUT_MS,
      }),
      keepUnusedDataFor: 60,
    }),

    createAdvertiser: builder.mutation<CreateAdvertiserResponse, CreateAdvertiserRequest>({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const parsed = createAdvertiserSchema.safeParse(body);
        if (!parsed.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: 'Invalid advertiser payload',
              data: { code: 'INVALID_PAYLOAD' as const, issues: parsed.error.issues },
            },
          };
        }
        const result = await baseQuery({
          url: 'v1/advertisers',
          method: 'POST',
          body: parsed.data,
        });
        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }
        return { data: result.data as CreateAdvertiserResponse };
      },
      // Portal layout gate + AuthContext read `/auth/me` — refresh User cache.
      invalidatesTags: [{ type: 'User', id: 'SELF' }],
    }),

    listVenueClaims: builder.query<ListVenueClaimsResponse, void>({
      query: () => 'v1/advertisers/me/venue-claims',
      providesTags: [{ type: 'VenueClaim', id: 'LIST' }],
    }),

    listClaimableVenues: builder.query<ListClaimableVenuesResponse, ListClaimableVenuesRequest>({
      query: ({ q, cityCode, limit } = {}) => ({
        url: 'v1/advertisers/me/claimable-venues',
        // Omitted params take the route's defaults (cityCode CLT, limit 25);
        // sending `undefined` explicitly would serialize as an empty string
        // and trip the strict query schema.
        params: {
          ...(q === undefined ? {} : { q }),
          ...(cityCode === undefined ? {} : { cityCode }),
          ...(limit === undefined ? {} : { limit }),
        },
      }),
      // Deliberately untagged: the venue catalogue is not advertiser state, so
      // no claim/listing mutation should invalidate it.
    }),

    listSponsorableEvents: builder.query<
      ListSponsorableEventsResponse,
      ListSponsorableEventsRequest
    >({
      query: ({ locationId, limit } = {}) => ({
        url: 'v1/advertisers/me/sponsorable-events',
        params: {
          ...(locationId === undefined ? {} : { locationId }),
          ...(limit === undefined ? {} : { limit }),
        },
      }),
      // What this returns is a function of which claims are APPROVED, so an
      // admin decision landing between steps must not be served from cache.
      providesTags: [{ type: 'VenueClaim', id: 'LIST' }],
    }),

    createVenueClaim: builder.mutation<VenueClaimResponse, CreateVenueClaimRequest>({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const parsed = createVenueClaimSchema.safeParse(body);
        if (!parsed.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: 'Invalid venue-claim payload',
              data: { code: 'INVALID_PAYLOAD' as const, issues: parsed.error.issues },
            },
          };
        }
        const result = await baseQuery({
          url: 'v1/advertisers/me/venue-claims',
          method: 'POST',
          body: parsed.data,
        });
        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }
        return { data: result.data as VenueClaimResponse };
      },
      invalidatesTags: [{ type: 'VenueClaim', id: 'LIST' }],
    }),

    listMySponsoredListings: builder.query<ListSponsoredListingsResponse, void>({
      query: () => 'v1/advertisers/me/sponsored-listings',
      providesTags: [{ type: 'SponsoredListing', id: 'LIST' }],
    }),

    createSponsoredListing: builder.mutation<
      SponsoredListingResponse,
      CreateSponsoredListingRequest
    >({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const parsed = createSelfServeSponsoredListingSchema.safeParse(body);
        if (!parsed.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: 'Invalid sponsored-listing payload',
              data: { code: 'INVALID_PAYLOAD' as const, issues: parsed.error.issues },
            },
          };
        }
        const result = await baseQuery({
          url: 'v1/advertisers/me/sponsored-listings',
          method: 'POST',
          body: parsed.data,
        });
        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }
        return { data: result.data as SponsoredListingResponse };
      },
      invalidatesTags: [{ type: 'SponsoredListing', id: 'LIST' }],
    }),

    createSponsoredCheckoutSession: builder.mutation<
      SponsoredCheckoutSessionResponse,
      CreateSponsoredCheckoutSessionRequest
    >({
      query: ({ listingId }) => ({
        url: `v1/advertisers/me/sponsored-listings/${encodeURIComponent(listingId)}/checkout-session`,
        method: 'POST',
      }),
      // The listing's Stripe state moves the moment Checkout completes; the
      // inbox must not keep showing a pre-payment snapshot when the advertiser
      // returns from the hosted page.
      invalidatesTags: [{ type: 'SponsoredListing', id: 'LIST' }],
    }),
  }),
});

export const {
  useCreateAdvertiserMutation,
  useListVenueClaimsQuery,
  useLazyListVenueClaimsQuery,
  useCreateVenueClaimMutation,
  useListClaimableVenuesQuery,
  useLazyListClaimableVenuesQuery,
  useListSponsorableEventsQuery,
  useLazyListSponsorableEventsQuery,
  useListMySponsoredListingsQuery,
  useLazyListMySponsoredListingsQuery,
  useCreateSponsoredListingMutation,
  useCreateSponsoredCheckoutSessionMutation,
  useGetSponsoredCheckoutStatusQuery,
} = advertiserEndpoints;

/**
 * Write `{ checkoutAvailable: false }` into the shared checkout-status cache
 * entry (WEB-1263). Dispatched when a checkout call answers 503
 * `CHECKOUT_UNAVAILABLE`: the server's own "closed" outranks a cached `true`,
 * and a refetch could be served the stale `true` from the browser's HTTP cache
 * (`Cache-Control: public, max-age=60`).
 *
 * Typed helper in the `upsertDiscoverEventsQueryData` mould (API-1720):
 * `injectEndpoints` does not widen `lociApi.util`'s declared type, and the
 * return is a dispatchable `UnknownAction` to keep declaration emit clean.
 */
export function markSponsoredCheckoutClosed(): UnknownAction {
  return advertiserEndpoints.util.upsertQueryData('getSponsoredCheckoutStatus', undefined, {
    checkoutAvailable: false,
  }) as unknown as UnknownAction;
}

/**
 * Narrow-typed endpoint slice exposed to tests (mirrors the `savedSearches.ts`
 * / `discover.ts` pattern — the inferred `injectEndpoints` type cannot be named
 * across the module boundary). Not part of the public API.
 */
interface AdvertiserEndpointSlice<TArg, TResult> {
  name: string;
  initiate: (arg: TArg) => (
    dispatch: unknown,
    getState: unknown,
    extra: unknown,
  ) => Promise<{ data?: TResult; error?: unknown; unwrap: () => Promise<TResult> }> & {
    unwrap: () => Promise<TResult>;
    /** Present on query subscriptions; unused for mutation slices. */
    unsubscribe: () => void;
  };
}

interface AdvertiserTestingEndpoints {
  getSponsoredCheckoutStatus: AdvertiserEndpointSlice<void, SponsoredCheckoutStatusResponse>;
  createAdvertiser: AdvertiserEndpointSlice<CreateAdvertiserRequest, CreateAdvertiserResponse>;
  listVenueClaims: AdvertiserEndpointSlice<void, ListVenueClaimsResponse>;
  createVenueClaim: AdvertiserEndpointSlice<CreateVenueClaimRequest, VenueClaimResponse>;
  listClaimableVenues: AdvertiserEndpointSlice<
    ListClaimableVenuesRequest,
    ListClaimableVenuesResponse
  >;
  listSponsorableEvents: AdvertiserEndpointSlice<
    ListSponsorableEventsRequest,
    ListSponsorableEventsResponse
  >;
  listMySponsoredListings: AdvertiserEndpointSlice<void, ListSponsoredListingsResponse>;
  createSponsoredListing: AdvertiserEndpointSlice<
    CreateSponsoredListingRequest,
    SponsoredListingResponse
  >;
  createSponsoredCheckoutSession: AdvertiserEndpointSlice<
    CreateSponsoredCheckoutSessionRequest,
    SponsoredCheckoutSessionResponse
  >;
}

/** Internal surface for package tests — not part of the public API. */
export const __testing = {
  endpoints: advertiserEndpoints.endpoints as unknown as AdvertiserTestingEndpoints,
};
